import { BASE_URL } from "../../../shared/consts/variables";
import { headerAuthorization } from "../../../shared/helpers/localstorage-helper";
import { Company } from "../../../shared/types/company";
import { SelectOption } from "../../../shared/types/select-options";

export interface SuperAdminCompaniesResponse {
    data: Company[];
    total: number;
}

export interface CompanyPayload {
    name: string;
    cnpj?: string | null;
    description?: string;
    isPlanActive?: boolean;
    licenses?: number;
    languageId?: string | null;
}

const parseResponseError = async (response: Response) => {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.message || `Erro HTTP: ${response.status}`) as Error & {
        status?: number;
        testsUsed?: number;
        requiresExplicitConfirmation?: boolean;
    };

    error.status = response.status;
    error.testsUsed = data.testsUsed;
    error.requiresExplicitConfirmation = data.requiresExplicitConfirmation;

    return error;
};

export const getCompaniesListSelect = async (): Promise<SelectOption[]> => {
    try {
        const response = await fetch(`${BASE_URL}/companies`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...headerAuthorization(),
            },
        });

        if (!response.ok) {
            throw new Error(`Erro HTTP: ${response.status}`);
        }

        const data = await response.json();
        
        return data.map((company: any) => ({
            id: company.id,
            label: company.name || company.description,
        }));
    } catch (error) {
        console.error('❌ Erro ao buscar empresas:', error);
        throw error;
    }
}

export const getSuperAdminCompaniesDashboard = async (): Promise<SuperAdminCompaniesResponse> => {
    const response = await fetch(`${BASE_URL}/super-admin/companies`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...headerAuthorization(),
        },
    });

    if (!response.ok) {
        throw await parseResponseError(response);
    }

    return response.json();
}

export const createSuperAdminCompany = async (company: CompanyPayload): Promise<Company> => {
    const response = await fetch(`${BASE_URL}/super-admin/companies`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...headerAuthorization(),
        },
        body: JSON.stringify(company),
    });

    if (!response.ok) {
        throw await parseResponseError(response);
    }

    return response.json();
}

export const updateSuperAdminCompany = async (id: string, company: CompanyPayload): Promise<Company> => {
    const response = await fetch(`${BASE_URL}/super-admin/companies/${id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...headerAuthorization(),
        },
        body: JSON.stringify(company),
    });

    if (!response.ok) {
        throw await parseResponseError(response);
    }

    return response.json();
}

export const deleteSuperAdminCompany = async (id: string, confirmDeleteWithTests = false) => {
    const response = await fetch(`${BASE_URL}/super-admin/companies/${id}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            ...headerAuthorization(),
        },
        body: JSON.stringify({ confirmDeleteWithTests }),
    });

    if (!response.ok) {
        throw await parseResponseError(response);
    }

    return response.json();
}
